import React, { Fragment, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { Controller, useForm } from 'react-hook-form';
import { FileUploader } from 'react-drag-drop-files';
import { Checkbox, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup } from '@mui/material';
import { formActions, httpRequestsOnSuccessActions, uploadFileWithPreview, authActions } from 'store';
import {
    CreateChancel,
    DateCalendarForm,
    Input,
    Loader,
    MaskInputCustom,
    MinLoader,
    SelectAutocompleteMultiple,
    TimeInput,
} from 'components';
import { isAutoSignature } from './formDetail/signatureFill.constants';
import {
    base64ToFile,
    FindLoad,
    FindSuccessItem,
    getNestedError,
    Images,
    KeyPurpose,
    OnlyNumber,
    useModal,
    attributeTypes, User,
    userTypeEnums,
} from 'utils';
import { inputsStyle } from '../../components/inputs/styles';
import { renderTimes } from '../../fragments/schedule/core/modals/constants';
import { UploadedSignature } from '../../fragments/schedule/core/common/uploadedSignature';
import { inputTypeEnums, selectTypeEnums } from './formDetail/attributes/constnats';
import { buildSelectionRules, selectionRequiredMark } from './formDetail/minSelections';
import {
    MentionableInput,
    RichTextElement,
    TableElement,
    FormSignature,
    SessionDataTableElement,
    AutoSignatureElement,
    GoalModeRichText,
    GoalModeTable,
} from './formDetail/formBody/elements';
import { ResizableImage } from './formDetail/formBody/elements/resizableImage';
import { InlineFieldsRichText } from './formDetail/formBody/elements/inlineFieldsRichText';
import { FormBuilderProvider, useFormBuilder } from './formDetail';
import { getWidth, isApptNoteModel, pickInitialTemplate } from './constants';
import { NoteTemplateField } from './noteTemplateField';
import { fillMentions, useAutoFillMentions } from './hooks/useAutoFillMentions';
import { resolveRichTextImages } from './utils/resolveRichTextImages';
import { hasInlineFields, inlineFieldsToText, richTextRequiredError, validateRichText } from './utils/inlineFields';
import { GET_FORMS } from '../../store/form/form.type';
import { ReportBuilder } from './progressReport/reportBuilder';
import { evaluateConditions } from './utils/evaluateConditions';
import { isReadOnlyBoxType } from './utils/fillableFields';
import { LockedField } from './utils/lockedField';
import { uploadPendingSignatures } from './utils/uploadPendingSignatures';
import { sectionTitleStyle, showSectionTitle } from './formDetail/section';

const DEFAULT_FORM_ACTION_TYPE = 'GET_FORM_BY_ID';
const DEFAULT_FORM_VALUES_ACTION_TYPE = 'GET_FORMS_DEFAULT';
const FORM_ACTION_TYPE = 'GET_FORMS';

const FormBuilderInner = forwardRef(
    (
        {
            info,
            resourceId,
            formModel,
            afterSuccess,
            ACTION_TYPE,
            handleSaveNote,
            handleSaveAction,
            sendParams = {},
            width,
            defaultForm,
            actionName,
            inviteToken,
            renderCustomFields,
            serviceTemplate,
            isClassicForm
        },
        ref,
    ) => {
        const { setFormList, imageUrls, setImageUrls } = useFormBuilder();
        const {
            handleSubmit,
            watch,
            control,
            setValue,
            getValues,
            clearErrors,
            reset,
            formState: { errors },
        } = useForm({ mode: 'onChange', reValidateMode: 'onChange' });
        const classes = inputsStyle();
        const forms = useSelector((state) => state.forms.forms)?.data;
        const formById = useSelector((state) => state.forms.form);
        const formDefault = useSelector((state) => state.forms.formDefault);
        const { userInfo } = User();
        const dispatch = useDispatch();
        const loader = FindLoad(ACTION_TYPE);
        const loadForm = FindLoad(DEFAULT_FORM_ACTION_TYPE);
        const loadDefaults = FindLoad(DEFAULT_FORM_VALUES_ACTION_TYPE);
        const success = FindSuccessItem(ACTION_TYPE);
        const { close } = useModal();
        const [uploadLoader, setUploadLoader] = useState(false);
        const [sigUploading, setSigUploading] = useState(false);
        const formRef = useRef();
        const reportBuilderRef = useRef(null);
        const applyAutoFill = useAutoFillMentions({ setValue, getValues });

        useEffect(() => {
            dispatch(authActions.getMyProfile());
        }, []);

        useImperativeHandle(ref, () => ({
            submit: () => {
                handleSubmit(
                    (data) => handleCreate(data),
                    (errors) => {
                        scrollToFirstError(errors);
                    },
                )();
            },
            /**
             * Path-based field setter used by AI streaming. Path is the full
             * RHF dotted path (`sections.X.attributes.Y.value`) — unambiguous
             * even when multiple attributes share the same `name`.
             */
            setFieldByPath: (path, value) => {
                return reportBuilderRef.current?.setFieldByPath?.(path, value) ?? false;
            },
        }));

        // useEffect(() => {
        //     dispatch(formActions.resetFormDefault());
        //     dispatch(formActions.resetForms());
        //
        //     if (formModel) {
        //         dispatch(formActions.getForms({ model: formModel }));
        //     }
        // }, []);

        useEffect(() => {
            if (formById?.id) {
                // Signed rich-text image URLs live in context, NOT formList/RHF
                // values (they change every read — see formContext imageUrls note).
                const { imageUrls: formImageUrls, ...cloned } = cloneDeep(formById);
                setImageUrls?.(formImageUrls || {});
                setFormList(cloned);
                const params = {
                    customForm: watch('customForm'),
                    formId: formById?.id,
                    ...cloned,
                };
                reset(params);
                applyAutoFill();
            }
        }, [formById]);

        const selectTemplate = (formId) => {
            setValue('customForm', formId);
            const params = {
                model: formModel,
                resourceId: resourceId,
                formId,
                ...sendParams,
            };
            dispatch(formActions.getFormDefault(formId, params));
            // Third arg only for session notes: it is the appt whose
            // collected session data fills SESSION_DATA_TABLE blocks.
            dispatch(formActions.getFormById(formId, params, isApptNoteModel(formModel) ? resourceId : undefined));
        };

        useEffect(() => {
            if (forms?.length && !watch('customForm') && !info?.id) {
                const selectedForm = pickInitialTemplate(forms, serviceTemplate);
                if (selectedForm) selectTemplate(selectedForm.id);
            }
        }, [forms]);

        useEffect(() => {
            const formId = info?.template?.formId;
            if (!info?.id || !formId) return;
            dispatch(formActions.resetFormDefault());
            dispatch(formActions.getFormDefault(formId, { model: formModel, resourceId, formId, ...sendParams }));
        }, [info?.id]);

        useEffect(() => {
            if (info?.template) {
                const { imageUrls: tplImageUrls, ...tpl } = cloneDeep(info.template);
                setImageUrls?.(tplImageUrls || {});
                setFormList(tpl);
                const params = {
                    name: info?.name,
                    ...tpl,
                    customForm: info?.id,
                };
                reset(params);
                applyAutoFill();
            }
            if (defaultForm) {
                const { imageUrls: dfImageUrls, ...df } = cloneDeep(defaultForm);
                setImageUrls?.(dfImageUrls || {});
                setFormList(df);
                reset({
                    customForm: df?.id,
                    formId: df?.id,
                    ...df,
                });
                applyAutoFill();
            }
        }, [info, defaultForm]);

        useEffect(() => {
            if (success) {
                close();
                dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));

                if (afterSuccess) {
                    afterSuccess();
                }
            }
        }, [success]);

        const handleCreate = async (data) => {
            if (isClassicForm) {
                setSigUploading(true);
                try {
                    await uploadPendingSignatures(data, inviteToken);
                } finally {
                    setSigUploading(false);
                }
                if (mergedHandleSaveNote) {
                    mergedHandleSaveNote(data);
                } else if (handleSaveAction) {
                    handleSaveAction(data);
                }
            } else {
                reportBuilderRef.current?.submit();
            }
        };

        /**
         * Wrap handleSaveNote so custom fields registered in FormBuilderInner's
         * form (e.g. "name", "sectionDescription") are merged into the data that
         * ReportBuilder produces from its own, separate useForm instance.
         */
        const mergedHandleSaveNote = handleSaveNote
            ? (reportData) => {
                  const outerValues = getValues();
                  handleSaveNote({
                      ...reportData,
                      name: outerValues.name ?? reportData.name,
                      sectionDescription: outerValues.sectionDescription ?? reportData.sectionDescription,
                  });
              }
            : undefined;

        const handleUploadFile = async (file, name) => {
            // Form image uploads go to the CLINICAL_DOC purpose — same
            // bucket + key namespace as other form-attached documents.
            // See `uploadFileWithPreview` docstring for the blob-URL
            // lifecycle and why `.url` stays across the edit session.
            const { previewUrl, storedFile } = uploadFileWithPreview(file, KeyPurpose.CLINICAL_DOC, { inviteToken });
            setValue(name, {
                url: previewUrl,
                originalName: file.name,
                format: (file.name.split('.').pop() || '').toLowerCase(),
            });
            setUploadLoader(true);
            try {
                setValue(name, { ...(await storedFile), url: previewUrl });
            } catch (e) {
                // Reset the field to null so the user retries from a clean
                // state instead of being stuck with the blob-URL ghost.
                setValue(name, null);
                throw e;
            } finally {
                setUploadLoader(false);
            }
        };

        // const getSignature = async (name) => {
        //     const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        //     if (sigCanvas?.current && signatureData && isDrawing) {
        //         const convertedFile = base64ToFile(signatureData, 'signature.png');
        //         if (convertedFile) {
        //             let formData = new FormData();
        //             formData.append('files', convertedFile);
        //             try {
        //                 const res = inviteToken
        //                     ? await UploadServices.InviteUploadSignature(formData, inviteToken)
        //                     : await UploadServices.UploadSignature(formData);
        //                 const info = {
        //                     file: {
        //                         ...res?.data,
        //                         name: 'signature',
        //                     },
        //                 };
        //                 setValue(name, info?.file);
        //             } catch (e) {
        //                 setUploadLoader(false);
        //             }
        //         }
        //     }
        // };

        const scrollToFirstError = (errors) => {
            if (!errors?.attributes && !errors?.sections) return;

            const flattenErrors = (data, path) => {
                if (data?.sections) {
                    for (let sIndex = 0; sIndex < data?.sections.length; sIndex++) {
                        const section = data?.sections[sIndex];
                        if (!section) continue;

                        if (section.attributes) {
                            const attrPath = flattenErrors(
                                { attributes: section?.attributes },
                                `${path || 'sections'}.${sIndex}.attributes`,
                            );
                            if (attrPath) return attrPath;
                        }
                    }
                }

                if (data?.attributes) {
                    const attributes = data.attributes;
                    for (let i = 0; i < attributes.length; i++) {
                        const item = attributes[i];
                        if (!item) continue;

                        const currentPath = `${path ? path : 'attributes'}.${i}`;

                        if (item?.value) return `${currentPath}.value`;

                        if (item?.fields) {
                            for (let fIndex = 0; fIndex < item?.fields?.length; fIndex++) {
                                const f = item.fields[fIndex];
                                if (f?.value) return `${currentPath}.fields.${fIndex}.value`;
                            }
                        }

                        if (item.children) {
                            const childPath = flattenErrors({ attributes: item.children }, `${currentPath}.children`);
                            if (childPath) return childPath;
                        }
                    }
                }
                return null;
            };

            const firstErrorField = flattenErrors(errors);

            if (firstErrorField) {
                // Or the first input nested under that value — a Session Data Table's
                // manual cells are named `<value>.<row>[.trials.<i>].manual.<column>`.
                const field = document.querySelector(`[name="${firstErrorField}"], [name^="${firstErrorField}."]`);
                if (field?.scrollIntoView) {
                    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    field.focus({ preventScroll: true });
                } else {
                    formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        };

        const renderInputs = (item, index, sectionIndex) => {
            const checkSection = sectionIndex || sectionIndex === 0;

            let namePrefix = `attributes.${index}`;
            if (checkSection) {
                namePrefix = `sections.${sectionIndex}.attributes.${index}`;
            }
            const name = `${namePrefix}.value`;
            const label = item?.label ? item?.label : '';
            const renderRichTextEditor = (value, onChange) => (
                <RichTextElement
                    item={item}
                    value={value}
                    onChange={onChange}
                    readOnly={false}
                    index={index}
                    wrapperStyle={{ border: '1px solid #0C66E4' }}
                    // Fresh signed URLs for rich-text image refs.
                    // Server-delivered on GET at the top of the
                    // form-builder / note response envelope.
                    imageUrls={imageUrls || {}}
                    // Register freshly-uploaded images into the
                    // shared context so they survive this editor
                    // instance unmounting (switch active attribute
                    // and back) before the first save round-trip.
                    onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                />
            );
            const renderTable = () => (
                <TableElement
                    item={item}
                    readOnly={readOnly}
                    name={name}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    errors={errors}
                    wrapperStyle={{ border: '1px solid #0C66E4' }}
                />
            );
            let required = item?.properties?.required;
            let readOnly = item?.properties?.readOnly;
            const itemWidth = getWidth(item?.properties?.width);
            const isInline = item?.properties?.inline;
            const isFullWidthType = [
                attributeTypes.RICH_TEXT,
                attributeTypes.TABLE,
                attributeTypes.SESSION_DATA_TABLE,
                attributeTypes.GRAPH,
                attributeTypes.TARGET,
            ].includes(item?.type);

            // Staff-only visibility: hide from non-staff users during form fill
            if (item?.properties?.staffOnly && userInfo?.userType !== userTypeEnums.STAFF) {
                return null;
            }

            // Conditional logic: evaluate rules against current section values
            if (item?.properties?.conditions?.length && checkSection) {
                const watchedSection = watch(`sections.${sectionIndex}`);
                const sectionAttrs = (watchedSection?.attributes || []).map((attr, i) => ({
                    ...attr,
                    value: attr?.value,
                }));
                const condResult = evaluateConditions(item.properties.conditions, sectionAttrs);
                if (!condResult.visible) return null;
                if (condResult.required) required = true;
                if (condResult.readOnly) readOnly = true;
            }

            // A read-only input or select: its value in a locked grey box, as in ReportBuilder.
            if (readOnly && isReadOnlyBoxType(item)) {
                return (
                    <LockedField
                        key={index}
                        item={item}
                        value={watch(name) ?? item?.value}
                        name={name}
                        label={item?.label}
                        required={required}
                        imageUrls={imageUrls}
                        style={{ width: isFullWidthType ? '100%' : itemWidth, boxSizing: 'border-box' }}
                        control={control}
                    />
                );
            }

            return (
                <div
                    key={index}
                    name={name}
                    className={readOnly ? 'report-builder-readonly-field' : undefined}
                    style={{ width: isFullWidthType ? '100%' : itemWidth, boxSizing: 'border-box' }}
                >
                    {item?.type === attributeTypes.RICH_TEXT && (
                        <>
                            {/* Read-only with inline fields: the text is locked, the fields are still filled (below). */}
                            {readOnly && !(hasInlineFields(item.value) && !item?.properties?.goalMode) ? (
                                <div className="mention-form-builder-wrapper" style={{ marginBottom: 18 }}>
                                    {item?.label && <p className="form-builder-mention-title">{`${label}${required ? '*' : ''}`}</p>}
                                    {(() => {
                                        // Inline fields read as their values; staff-only ones are left out for clients.
                                        const htmlContent = inlineFieldsToText(item.value || '', {
                                            hideStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                        });
                                        const strippedText = htmlContent
                                            .replace(/<[^>]*>/g, '')
                                            .replace(/&nbsp;/g, ' ')
                                            .trim();
                                        const hasContent = strippedText.length > 0 || /<img\s/i.test(htmlContent);
                                        return (
                                            <div className="form-builder-mention-input" style={{ minHeight: 40 }}>
                                                {hasContent ? (
                                                    <div
                                                        className="richtext-readonly-content"
                                                        dangerouslySetInnerHTML={{ __html: resolveRichTextImages(htmlContent, imageUrls) }}
                                                    />
                                                ) : (
                                                    <span style={{ color: '#98a2b3' }}>{item?.placeholder || ''}</span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            ) : (
                                // One rule for every editable variant (plain, inline fields, Goal Mode): the
                                // inline fields' own checks, then `required` (`validateRichText`).
                                <Controller
                                    name={name}
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            // Goal Mode: until a goal is picked the value is still the bare template.
                                            const goalIds = getValues(`${name.replace(/\.value$/, '')}.goalModeGoalIds`);
                                            if (required && item?.properties?.goalMode && !goalIds?.length) return richTextRequiredError(label);
                                            return validateRichText(value, {
                                                label,
                                                required,
                                                skipStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                            });
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => {
                                        const withInlineFields = hasInlineFields(item.value) && !item?.properties?.goalMode;
                                        // Re-checked on change only once the submit has flagged it, as on the
                                        // progress report: an inline control shows a mistyped value itself.
                                        const handleChange = (html) => setValue(name, html, { shouldDirty: true, shouldValidate: !!error });
                                        return (
                                            <>
                                                <p className="form-builder-attribute-title" style={error ? { color: '#FE7070' } : {}}>
                                                    {`${label}${required ? '*' : ''}`}
                                                </p>
                                                <div style={{ marginBottom: 18 }}>
                                                    {withInlineFields && readOnly ? (
                                                        // Read-only text: drawn as it is, each inline field still a control staff fill.
                                                        <div className="form-builder-mention-input">
                                                            <InlineFieldsRichText
                                                                html={field.value ?? item.value}
                                                                onChange={handleChange}
                                                                hideStaffOnly={userInfo?.userType !== userTypeEnums.STAFF}
                                                                showErrors={!!error}
                                                                imageUrls={imageUrls || {}}
                                                            />
                                                        </div>
                                                    ) : withInlineFields ? (
                                                        // Inline fields (Input / Date / Select / Checkbox / Radio inside the text):
                                                        // the editor with each field as its control, staff type and format the text too.
                                                        <RichTextElement
                                                            item={item}
                                                            value={field.value ?? item.value}
                                                            onChange={handleChange}
                                                            readOnly={false}
                                                            wrapperStyle={{ border: '1px solid #0C66E4' }}
                                                            imageUrls={imageUrls || {}}
                                                            onImageUpload={(key, url) => setImageUrls?.((prev) => ({ ...prev, [key]: url }))}
                                                            fill={{
                                                                hideStaffOnly: userInfo?.userType !== userTypeEnums.STAFF,
                                                                showErrors: !!error,
                                                            }}
                                                        />
                                                    ) : item?.properties?.goalMode ? (
                                                        // Goal Mode: the text filled once per goal staff pick.
                                                        <GoalModeRichText
                                                            item={item}
                                                            name={name}
                                                            watch={watch}
                                                            getValues={getValues}
                                                            setValue={setValue}
                                                            renderEditor={renderRichTextEditor}
                                                        />
                                                    ) : (
                                                        renderRichTextEditor(field.value ?? item.value, handleChange)
                                                    )}
                                                    {/* Inline-field errors are listed under the editor by the fields themselves. */}
                                                    {error?.message && (!withInlineFields || error.message === richTextRequiredError(label)) && (
                                                        <p className="error-message">{error.message}</p>
                                                    )}
                                                </div>
                                            </>
                                        );
                                    }}
                                />
                            )}
                        </>
                    )}

                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.TEXT && (
                        <Controller
                            name={name}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (!required) return true;
                                    if (!value) return `${label} is required.`;
                                    const stripped = value
                                        .replace(/<[^>]*>/g, '')
                                        .replace(/&nbsp;/g, ' ')
                                        .trim();
                                    return stripped.length > 0 || `${label} is required.`;
                                },
                            }}
                            render={({ field, formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                return (
                                    <div className="mention-form-builder-wrapper">
                                        {item?.label && (
                                            <p className="form-builder-mention-title" style={error?.message ? { color: '#FE7070' } : {}}>
                                                {`${label}${required ? '*' : ''}`}
                                            </p>
                                        )}
                                        <MentionableInput
                                            className={`form-builder-mention-input ${error ? 'error' : ''}`}
                                            defaultValue={field.value || watch(name)}
                                            value={field.value || watch(name)}
                                            onChange={(html) => {
                                                field.onChange(html);
                                            }}
                                            readOnly={readOnly}
                                            singleLine
                                            error={error?.message}
                                            placeholder={item?.placeholder}
                                        />
                                    </div>
                                );
                            }}
                        />
                    )}

                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.NUMBER && (
                        <Input
                            name={name}
                            label={`${label}${required ? '*' : ''}`}
                            variant="outlined"
                            control={control}
                            readOnly={readOnly}
                            placeholder={item?.placeholder}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    )}
                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.EMAIL && (
                        <Input
                            helperText={item?.description ? item?.description : ''}
                            name={name}
                            label={`${label}${required ? '*' : ''}`}
                            variant="outlined"
                            type={'email'}
                            control={control}
                            readOnly={readOnly}
                            placeholder={item?.placeholder}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    )}
                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.PHONE && (
                        <MaskInputCustom
                            helperText={item?.description ? item?.description : ''}
                            name={name}
                            label={`${label}${required ? '*' : ''}`}
                            variant="outlined"
                            control={control}
                            readOnly={readOnly}
                            rules={{
                                required: required ? `${label} is required.` : false,
                                pattern: {
                                    value: OnlyNumber,
                                    message: 'Please enter a valid 10 digit number.',
                                },
                                minLength: {
                                    value: 10,
                                    message: 'Please enter a valid 10 digit number.',
                                },
                            }}
                        />
                    )}
                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.DATE && (
                        <DateCalendarForm
                            name={name}
                            readOnly={readOnly}
                            label={`${label}${required ? '*' : ''}`}
                            control={control}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                        />
                    )}
                    {item?.type === attributeTypes.INPUT && item?.defaultDateType === inputTypeEnums.TIME && (
                        <Controller
                            name={name}
                            control={control}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                            render={({ formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                return (
                                    <TimeInput
                                        hhWidth={'25px'}
                                        mmWidth={'30px'}
                                        aPMWidth={'60px'}
                                        labelColor={error?.message ? '#FE7070' : '#347AF0'}
                                        label={`${label}${required ? '*' : ''}`}
                                        disabled={readOnly}
                                        name={name}
                                        typeError={error?.message}
                                        onChange={(ev) => setValue(name, ev?.target?.value)}
                                        defaultValue={
                                            watch(name) ? (watch(name)?.length > 10 ? renderTimes(watch(name)) : watch(name)) : null
                                        }
                                    />
                                );
                            }}
                        />
                    )}

                    {item?.type === attributeTypes.CHECK_BOX && !!item?.properties?.options?.length > 0 && (
                        <FormControl component="fieldset" style={{ marginBottom: 18 }}>
                            <Controller
                                name={name}
                                control={control}
                                defaultValue={Array.isArray(item?.value) ? item.value : []}
                                rules={buildSelectionRules(item, required, label)}
                                render={({ field, formState: { errors } }) => {
                                    const error = getNestedError(errors, name);
                                    const selected = Array.isArray(field.value) ? field.value : [];
                                    const toggleOption = (option) => {
                                        const isSelected = selected.some((s) => s.id === option.id);
                                        const updated = isSelected
                                            ? selected.filter((s) => s.id !== option.id)
                                            : [...selected, { name: option.name, id: option.id }];
                                        field.onChange(updated);
                                    };

                                    return (
                                        <FormGroup>
                                            <p
                                                className="form-builder-attribute-title"
                                                style={error?.message ? { color: '#FE7070' } : {}}
                                            >{`${item?.label}${selectionRequiredMark(item, required)}`}</p>
                                            {item?.properties?.options?.map((option) => (
                                                <FormControlLabel
                                                    disabled={readOnly}
                                                    key={option?.id}
                                                    control={
                                                        <Checkbox
                                                            style={{
                                                                color: error?.message ? '#FE7070' : '#0C66E4',
                                                                padding: '2px 8px',
                                                            }}
                                                            checked={selected.some((s) => s.id === option.id)}
                                                            onChange={() => toggleOption(option)}
                                                        />
                                                    }
                                                    label={option.name}
                                                />
                                            ))}
                                            {error?.message && <p className="error-message">{error.message}</p>}
                                        </FormGroup>
                                    );
                                }}
                            />
                        </FormControl>
                    )}

                    {item?.type === attributeTypes.RADIO_BUTTON && !!item?.properties?.options?.length > 0 && (
                        <FormControl component="fieldset" style={{ marginBottom: 18 }}>
                            <Controller
                                name={name}
                                control={control}
                                defaultValue={item?.value || null}
                                rules={{ required: required ? `${item?.name} is required.` : false }}
                                render={({ field, formState: { errors } }) => {
                                    const error = getNestedError(errors, name);
                                    const currentValue = field.value?.id || '';

                                    return (
                                        <RadioGroup
                                            {...field}
                                            value={currentValue}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                const selectedOption = item?.properties?.options?.find((opt) => opt.id === selectedId);
                                                field.onChange(
                                                    selectedOption
                                                        ? {
                                                              name: selectedOption.name,
                                                              id: selectedOption.id,
                                                          }
                                                        : null
                                                );
                                            }}>
                                            <p className="form-builder-attribute-title">{`${item?.label}${required ? '*' : ''}`}</p>
                                            {item?.properties?.options?.map((option) => (
                                                <FormControlLabel
                                                    disabled={readOnly}
                                                    className={classes.radioInputLabel}
                                                    key={option.id}
                                                    value={option.id}
                                                    control={
                                                        <Radio
                                                            disableRipple
                                                            style={{
                                                                color: error?.message ? '#FE7070' : '#0C66E4',
                                                                padding: '2px 8px 2px 9px',
                                                            }}
                                                            classes={{
                                                                root: classes.radio,
                                                                checked: classes.checked,
                                                            }}
                                                        />
                                                    }
                                                    label={option.name}
                                                />
                                            ))}
                                        </RadioGroup>
                                    );
                                }}
                            />
                        </FormControl>
                    )}

                    {item?.type === attributeTypes.SELECT &&
                        item?.defaultDateType === selectTypeEnums.MULTIPLE &&
                        !!item?.properties?.options?.length > 0 && (
                            <SelectAutocompleteMultiple
                                readOnly={readOnly}
                                defaultSelected={item?.value || []}
                                options={item?.properties?.options || []}
                                multiple={true}
                                label={`${item?.label}${selectionRequiredMark(item, required)}`}
                                name={name}
                                renderValue={(option) => option?.name || option?.value}
                                control={control}
                                rules={buildSelectionRules(item, required, label)}
                                placeholder={item?.placeholder}
                            />
                        )}

                    {item?.type === attributeTypes.SELECT &&
                        item?.defaultDateType === selectTypeEnums.SINGLE &&
                        !!item?.properties?.options?.length > 0 && (
                            <SelectAutocompleteMultiple
                                readOnly={readOnly}
                                defaultSelected={item?.value && { name: item?.value?.name }}
                                options={item?.properties?.options || []}
                                label={`${item?.label}${required ? '*' : ''}`}
                                name={name}
                                renderValue={(option) => option?.name || option?.value}
                                control={control}
                                // The picked option itself goes through `field.onChange`; a `setValue` after it made
                                // react-hook-form drop the validation, leaving "required" shown after picking.
                                fullValue
                                multiline
                                rules={{ required: required ? `${item?.name} is required.` : false }}
                                placeholder={item?.placeholder}
                            />
                        )}

                    {item?.type === attributeTypes.UPLOAD && (
                        <Controller
                            name={name}
                            control={control}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                            render={({ field, formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                const fileValue = watch(name);
                                const isImage =
                                    fileValue &&
                                    /\.(svg|jpe?g|png|gif|webp|bmp)$/i.test(
                                        typeof fileValue === 'string'
                                            ? fileValue
                                            : fileValue?.url || fileValue?.originalName || fileValue?.name || ''
                                    );
                                return (
                                    <div style={{ marginBottom: 12 }}>
                                        {isImage ? (
                                            <div className="builder-uploaded-image">
                                                <div className="space-between" style={{ width: '100%', justifyContent: 'space-between' }}>
                                                    <p
                                                        className="preview-creator-info-line-title"
                                                        style={
                                                            error?.message
                                                                ? {
                                                                      color: '#FE7070',
                                                                      marginBottom: 0,
                                                                  }
                                                                : { marginBottom: 0 }
                                                        }>
                                                        {`${label}${required ? '*' : ''}`}
                                                    </p>
                                                    {!readOnly ? (
                                                        <button
                                                            onClick={() => {
                                                                field.onChange(null);
                                                                setValue(name, null);
                                                            }}
                                                            className="reset-btn-by-default">
                                                            <img src={Images.redTrash} alt="redTrash" />
                                                        </button>
                                                    ) : (
                                                        <div />
                                                    )}
                                                </div>
                                                <div>
                                                    <ResizableImage
                                                        src={fileValue?.url}
                                                        alt={'uploaded-image'}
                                                        style={{ maxHeight: 300 }}
                                                        initialWidth={fileValue?.resizedWidth}
                                                        initialHeight={fileValue?.resizedHeight}
                                                        onResize={({ width, height }) => {
                                                            setValue(name, {
                                                                ...fileValue,
                                                                resizedWidth: width,
                                                                resizedHeight: height,
                                                            });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <p
                                                    style={error?.message ? { color: '#FE7070' } : {}}
                                                    className="form-builder-attribute-title">
                                                    {`${label}${required ? '*' : ''}`}
                                                </p>
                                                <FileUploader
                                                    disabled={readOnly}
                                                    handleChange={(file) => {
                                                        handleUploadFile(file, name);
                                                        clearErrors(name);
                                                    }}
                                                    name={name}>
                                                    <div
                                                        className="drag-drop-component"
                                                        style={error?.message ? { borderColor: '#FE7070' } : {}}>
                                                        <div style={{ height: 50 }}>
                                                            {uploadLoader ? (
                                                                <MinLoader
                                                                    margin={'0'}
                                                                    width={'36px'}
                                                                    height={'36px'}
                                                                    color={'#475467'}
                                                                    position={'relative'}
                                                                    style={{ marginBottom: 12 }}
                                                                />
                                                            ) : (
                                                                <img src={Images.uploadCloudBlack} alt="icon" />
                                                            )}
                                                        </div>
                                                        <label className="custom-file-upload">
                                                            <input
                                                                disabled={readOnly}
                                                                onClick={(event) => (event.target.value = null)}
                                                                onChange={(file) => {
                                                                    handleUploadFile(file, name);
                                                                    clearErrors(name);
                                                                }}
                                                                type="file"
                                                                name={name}
                                                                id="file"
                                                            />
                                                            <i className="fa fa-cloud-upload" />

                                                            <div className="upload">
                                                                {!readOnly && (
                                                                    <p>
                                                                        <span
                                                                            style={{
                                                                                color: '#2E6DD8',
                                                                                fontWeight: 600,
                                                                            }}>
                                                                            Click to upload
                                                                        </span>{' '}
                                                                        or drag and drop
                                                                    </p>
                                                                )}
                                                                <p style={{ fontSize: '12px', color: '#172B4D' }}>
                                                                    PDF, PNG, JPG and CSV (max. 800x400px)
                                                                </p>
                                                            </div>
                                                        </label>
                                                    </div>
                                                </FileUploader>
                                                <p className="error-message">{error?.message}</p>

                                                {watch(name) && (
                                                    <UploadedSignature
                                                        noDel={readOnly}
                                                        handleRemoveUploaded={() => {
                                                            field.onChange(null);
                                                            setValue(name, null);
                                                        }}
                                                        alreadyUploaded={true}
                                                        currentFile={{ file: watch(name) }}
                                                        alwaysDelete={true}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    )}

                    {item?.type === attributeTypes.SIGNATURE && isAutoSignature(item?.properties) && (
                        <div>
                            <p className="form-builder-attribute-title">{label}</p>
                            <AutoSignatureElement item={item} />
                        </div>
                    )}

                    {item?.type === attributeTypes.SIGNATURE && !isAutoSignature(item?.properties) && (
                        <Controller
                            name={name}
                            control={control}
                            rules={{
                                required: required ? `${label} is required.` : false,
                            }}
                            render={({ field, formState: { errors } }) => {
                                const error = getNestedError(errors, name);
                                return (
                                    <div>
                                        <p style={error?.message ? { color: '#FE7070' } : {}} className="form-builder-attribute-title">
                                            {`${label}${required ? '*' : ''}`}
                                        </p>
                                        <FormSignature
                                            onSignatureChange={(pendingSignature) => {
                                                field.onChange(pendingSignature);
                                                setValue(name, pendingSignature);
                                                if (pendingSignature) clearErrors(name);
                                            }}
                                            currentValue={watch(name) || item?.value}
                                            readOnly={readOnly}
                                            hasError={!!error?.message}
                                        />
                                        <p className="error-message">{error?.message}</p>
                                    </div>
                                );
                            }}
                        />
                    )}

                    {item?.type === attributeTypes.SESSION_DATA_TABLE && (
                        <div>
                            <p className="form-builder-attribute-title">{label}</p>
                            {/* AUTO cells are session data; only MANUAL columns take staff
                                input, written per row under `<name>.<row>.manual`.
                                `properties.readOnly` is ignored — see ReportBuilder. */}
                            <SessionDataTableElement item={item} index={index} readOnly={false} control={control} errors={errors} name={name} />
                        </div>
                    )}

                    {item?.type === attributeTypes.TABLE && (
                        <div>
                            <p className="form-builder-attribute-title">{label}</p>
                            {item?.properties?.goalMode && !readOnly ? (
                                <GoalModeTable
                                    name={name}
                                    watch={watch}
                                    getValues={getValues}
                                    setValue={setValue}
                                    targetMode={!!item?.properties?.targetMode}
                                >
                                    {renderTable()}
                                </GoalModeTable>
                            ) : (
                                renderTable()
                            )}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div style={{ width: width ? width : 816 }}>
                <div className="form-builder">
                    <form
                        onSubmit={handleSubmit(
                            (data) => handleCreate(data),
                            (errors) => {
                                scrollToFirstError(errors);
                            },
                        )}
                    >
                            {!defaultForm && (
                                <>
                                    <NoteTemplateField
                                        serviceTemplate={serviceTemplate}
                                        info={info}
                                        forms={forms}
                                        control={control}
                                        loadType={FORM_ACTION_TYPE}
                                        onSelect={selectTemplate}
                                        onClear={() => dispatch(formActions.resetFormDefault())}
                                    />
                                    {renderCustomFields && <>{renderCustomFields(control, watch('customForm'))}</>}
                                </>
                            )}

                    {loadForm?.length || loadDefaults?.length ? (
                        <Loader height={'200px'} />
                    ) : (
                        watch('customForm') && (
                            isClassicForm ?
                                <div ref={formRef}>
                                    {watch()?.sections?.map((section, sIndex) => {
                                        if (!section?.attributes?.length) return null;
                                        return (
                                            <div key={sIndex} className="form-section-builder">
                                                {showSectionTitle(section) && (
                                                    <div>
                                                        <p className="form-builder-section-title" style={sectionTitleStyle(section)}>{section?.name}</p>
                                                        <div className="form-builder-section-line" />
                                                    </div>
                                                )}

                                                <div style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    columnGap: '12px',
                                                    width: '100%',
                                                }}>
                                                    {section?.attributes?.map((item, index) => (
                                                        <React.Fragment key={index}>{renderInputs(item, index, sIndex)}</React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                :
                                <ReportBuilder
                                    resourceId={resourceId}
                                    ref={reportBuilderRef}
                                    handleSaveNote={mergedHandleSaveNote}
                                    noteStatus={info?.status}
                                    onSigUploadingChange={setSigUploading}
                                />
                        )
                    )}

                    <div className="submit-form-actions">
                            <CreateChancel
                                disabled={(!!loadForm?.length || !!loadDefaults?.length || !!loader.length || sigUploading || uploadLoader)}
                                type="submit"
                                loader={!!loader.length || sigUploading || uploadLoader}
                                create={actionName ? actionName : info?.id ? 'Save' : 'Add'}
                                chancel={'Cancel'}
                                onClose={close}
                                onCreate={isClassicForm ? undefined : handleSaveAction}
                                buttonWidth="100%"
                            />
                        </div>
                    </form>
                </div>
            </div>
        );
    },
);

export const FormBuilder = forwardRef((props, ref) => (
    <FormBuilderProvider>
        <FormBuilderInner {...props} ref={ref} />
    </FormBuilderProvider>
));
